import React, { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import { collectDistributionErrors } from 'utils/assist/collectDistributionErrors';
import { useExecuteAssistMigration } from 'api/queries/assist/useExecuteAssistMigration';
import { usePreviewAssistMigration } from 'api/queries/assist/usePreviewAssistMigration';
import {
  AssistMigrationDistributionPreview,
  AssistMigrationExecuteResult,
  AssistMigrationPreview,
} from 'types/assist/assistMigration';
import { ConfirmDiscardDialog } from './ConfirmDiscardDialog';
import { ConfirmRunDialog } from './ConfirmRunDialog';
import { MigrationPreview } from './MigrationPreview';
import { MigrationPreviewError } from './MigrationPreviewError';
import { MigrationResult } from './MigrationResult';

interface AssistMigrationModalProps {
  businessId: number | null;
  businessName: string;
  open: boolean;
  onClose: () => void;
  onMigrated: () => void;
}

export const AssistMigrationModal = ({
  businessId,
  businessName,
  open,
  onClose,
  onMigrated,
}: AssistMigrationModalProps) => {
  const [preview, setPreview] = useState<AssistMigrationPreview | null>(null);
  const [distribution, setDistribution] = useState<AssistMigrationDistributionPreview | null>(null);
  const [executeResult, setExecuteResult] = useState<AssistMigrationExecuteResult | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [confirmRunOpen, setConfirmRunOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const previewMutation = usePreviewAssistMigration();
  const executeMutation = useExecuteAssistMigration({
    successCallback: (result) => {
      setExecuteResult(result);
      onMigrated();
    },
  });

  const loadPreview = () => {
    if (!businessId) {
      return;
    }

    setPreview(null);
    setDistribution(null);
    setExecuteResult(null);
    setIsDirty(false);
    previewMutation
      .mutateAsync({ businessId })
      .then((result) => {
        setPreview(result);
        const resultDistribution = result?.distribution;

        if (!resultDistribution) {
          setDistribution(null);

          return;
        }

        setDistribution({
          ...resultDistribution,
          businessSettings: resultDistribution.businessSettings ?? {
            welcomeMessage: result?.source?.welcomeMessage ?? null,
            escalationPhoneNumber: result?.source?.escalationPhoneNumber ?? null,
          },
        });
      })
      // Error is surfaced by the mutation's onError toast and the in-panel error state.
      .catch(() => undefined);
  };

  useEffect(() => {
    if (!open || !businessId) {
      return;
    }

    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, businessId]);

  const validationErrors = useMemo(
    () => (distribution ? collectDistributionErrors(distribution) : []),
    [distribution],
  );

  const pendingActions = preview?.actions.filter((action) => !action.alreadySatisfied) ?? [];

  const handleConfirm = () => {
    if (!businessId) {
      return;
    }

    setConfirmRunOpen(false);
    executeMutation.mutate({ businessId, distribution });
  };

  // Guard unsaved edits: skip the prompt once the migration has run or nothing was edited.
  const handleRequestClose = () => {
    if (executeMutation.isLoading) {
      return;
    }
    if (isDirty && !executeResult) {
      setConfirmDiscardOpen(true);
      return;
    }
    onClose();
  };

  const updateStore = (
    storeId: number,
    patch: Partial<AssistMigrationDistributionPreview['stores'][number]>,
  ) => {
    setIsDirty(true);
    setDistribution((current) =>
      current
        ? {
            ...current,
            stores: current.stores.map((store) =>
              store.storeId === storeId ? { ...store, ...patch } : store,
            ),
          }
        : current,
    );
  };

  const updateGlobal = (patch: Partial<AssistMigrationDistributionPreview['global']>) => {
    setIsDirty(true);
    setDistribution((current) =>
      current ? { ...current, global: { ...current.global, ...patch } } : current,
    );
  };

  const updateBusinessSettings = (
    patch: Partial<AssistMigrationDistributionPreview['businessSettings']>,
  ) => {
    setIsDirty(true);
    setDistribution((current) => {
      if (!current) {
        return current;
      }

      const previousTransferNumber = current.businessSettings.escalationPhoneNumber;
      const nextTransferNumber = patch.escalationPhoneNumber;
      const shouldUpdateStoreTransferNumbers = nextTransferNumber !== undefined;
      const stores = shouldUpdateStoreTransferNumbers
        ? current.stores.map((store) =>
            store.escalationPhoneNumber === previousTransferNumber
              ? { ...store, escalationPhoneNumber: nextTransferNumber }
              : store,
          )
        : current.stores;

      return {
        ...current,
        businessSettings: { ...current.businessSettings, ...patch },
        stores,
      };
    });
  };

  const handleDiscard = () => {
    setConfirmDiscardOpen(false);
    onClose();
  };

  const showPreviewError = !previewMutation.isLoading && !executeResult && previewMutation.isError;
  const showPreview =
    !previewMutation.isLoading && !executeResult && !previewMutation.isError && !!preview;

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={handleRequestClose}
      PaperProps={{ sx: { width: '100vw', maxWidth: '100vw' } }}
    >
      <DialogTitle>Migrate {businessName} to Assist 2.0</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', overflow: 'auto' }}>
        <div
          style={{
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
          }}
        >
          {previewMutation.isLoading && (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Distributing the data across stores — this may take more than a couple of seconds.
              </Typography>
            </div>
          )}
          {showPreviewError && <MigrationPreviewError onRetry={loadPreview} />}
          {showPreview && preview && (
            <MigrationPreview
              preview={preview}
              distribution={distribution}
              validationErrors={validationErrors}
              onUpdateBusinessSettings={updateBusinessSettings}
              onUpdateGlobal={updateGlobal}
              onUpdateStore={updateStore}
            />
          )}
          {executeResult && <MigrationResult result={executeResult} />}
        </div>
      </DialogContent>
      <DialogActions>
        {!executeResult ? (
          <>
            <Button onClick={handleRequestClose} disabled={executeMutation.isLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={
                !preview?.isMigratable || executeMutation.isLoading || validationErrors.length > 0
              }
              onClick={() => setConfirmRunOpen(true)}
            >
              {executeMutation.isLoading ? <CircularProgress size={20} /> : 'Run migration'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        )}
      </DialogActions>

      <ConfirmRunDialog
        open={confirmRunOpen}
        businessName={businessName}
        storeCount={preview?.profile.storeCount ?? 0}
        pendingActions={pendingActions}
        onCancel={() => setConfirmRunOpen(false)}
        onConfirm={handleConfirm}
      />

      <ConfirmDiscardDialog
        open={confirmDiscardOpen}
        onKeepEditing={() => setConfirmDiscardOpen(false)}
        onDiscard={handleDiscard}
      />
    </Drawer>
  );
};
